<template>
  <div class="ca-highlight-section-bg" :class="highlightBackgroundClass">
    <div class="ca-bullet-container">
      <div class="ca-highlight-section pt-size-9 pb-size-10">
        <nav aria-label="Highlight Section" class="flex flex-col gap-size-7">
          <CaModuleMessage
            :label="model.label"
            :description="model.description"
            :title="model.title"
            :title-heading-type="model.titleHeadingType || 'H2'"
            :description-heading-type="model.descriptionHeadingType"
          />

          <CaMediaContent
            :type="model.mediaType"
            :image="model.imageUrl"
            :imageMobile="model.imageMobileUrl"
            :videoSrc="model.videoUrl"
            :youtubeUrl="model.thumbnailVideoUrl"
            :thumbnail="model.thumbnailImageUrl"
            :thumbnailMobile="model.thumbnailImageMobileUrl"
            :duration="model.thumbnailVideoDuration"
            :label="model.thumbnailLabel"
            :title="model.thumbnailTitle"
            :title-heading-type="model.thumbnailTitleHeadingType"
            :description="model.thumbnailDescription"
            :description-heading-type="model.thumbnailDescriptionHeadingType"
            :buttonLabel="model.thumbnailButtonLabel"
            :alt="model.label"
          />

          <div
            v-if="cards.length"
            class="ca-quick-action-cards grid grid-cols-1 gap-size-5"
            :style="{ '--ca-desktop-cols': desktopColumns }"
          >
            <CaQuickActionCard
              v-for="(card, index) in cards"
              :key="index"
              class="ca-quick-action-cards__card"
              :class="{
                'ca-quick-action-cards__card--full': isFullWidth(index),
              }"
              :title="card.title"
              :title-heading-type="card.titleHeadingType"
              :description="card.description"
              :description-heading-type="card.descriptionHeadingType"
              :backgroundColor="normalizeColor(card.color)"
              :image="card.iconUrl"
              :avatarBackgroundColorClass="card.iconBackgroundColor"
              :url="card.url"
              :target="card.target"
              :isBackgroundGreen="isBackgroundGreen"
            />
          </div>
          <!-- Loading: shimmer pill in place of the CTA button -->
          <div
            v-if="isLoading"
            class="ca-highlight-section__button-shimmer self-start"
            aria-hidden="true"
          ></div>
          <BaseButton
            v-else-if="model.buttonUrl"
            :label="model.buttonLabel"
            type="theme"
            variant="primary"
            class="theme primary flex self-start"
            @click="handleButtonClick"
          >
            <template #leadingIcon>
              <IconComponent name="arrow_right" size="2" />
            </template>
          </BaseButton>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
const isDev = import.meta.env.DEV;
</script>

<script setup>
import { ref, computed, onMounted } from "vue";
import CaQuickActionCard from "../../base/cards/CaQuickActionCard.vue";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
import CaMediaContent from "../../base/mediaContent/CaMediaContent.vue";
import { BaseButton, IconComponent } from "ca-online-design-system";

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

// Para import dinâmico em dev, evitando incluir o mock no bundle de produção
const modelRef = ref(props.model ?? null);
const model = computed(() => modelRef.value || {});

const cards = computed(() => model.value.cards ?? []);

/**
 * Loading state derived internally: while the essential content (the title)
 * hasn't arrived yet, the section renders shimmer skeletons. Mirrors the
 * behaviour of CaQuickActionCard.
 */
const isLoading = computed(() => !model.value.title);

const normalizedSize = computed(() => {
  const size = String(model.value.size || "S").toUpperCase();
  return ["S", "M", "L"].includes(size) ? size : "S";
});

const backgroundVariants = {
  laranja: "laranja",
  verde: "verde",
  branco: "branco",
};

/** Normalize a model color (e.g. "verde") to the base card variant. */
function normalizeColor(color) {
  return backgroundVariants[String(color || "").toLowerCase()] ?? "branco";
}

/** Navigate to the URL provided in model.buttonUrl. */
function handleButtonClick() {
  const url = model.value.buttonUrl;
  if (!url) return;
  window.location.href = url;
}

/**
 * When the number of cards is odd, the last card spans the full width
 * (both columns). Covers the 1-card and 3-card cases.
 */
function isFullWidth(index) {
  const total = cards.value.length;
  return total % 2 === 1 && index === total - 1;
}

/**
 * On desktop the grid has as many columns as there are cards (capped at 4),
 * so 1, 2 or 3 cards always fill the full width of the container.
 */
const desktopColumns = computed(() => Math.min(cards.value.length || 1, 4));

/** Container background variants (includes a neutral/grey option). */
const containerBackgroundVariants = {
  white: "white",
  solarized: "solarized",
};

/** Highlight section background variants coming from the model. */
const highlightBackgroundVariants = {
  green: "green",
  solarized: "solarized",
};

/** Background applied to the .ca-highlight-section, coming from the model. */
const highlightBackgroundClass = computed(() => {
  const variant = String(model.value.backgroundColor || "").toLowerCase();
  const mapped = highlightBackgroundVariants[variant];
  return mapped ? `ca-highlight-section--${mapped}` : "";
});

/** True when the section background is green, propagated to the cards. */
const isBackgroundGreen = computed(
  () => String(model.value.backgroundColor || "").toLowerCase() === "green",
);

onMounted(async () => {
  // Em desenvolvimento, se não receber um model via props, carrega o mock para facilitar testes
  if (isDev && !modelRef.value) {
    const { mockActionCards3 } =
      await import("./mocks/highlightSectionMocks.js");
    modelRef.value = mockActionCards3;
  }
});
</script>

<style scoped lang="scss">
// ---------------------------------------------------------------------------
// Section padding (mobile first). Base vertical padding (size-9) is applied via
// DS utility classes in the template. The DS stylesheet does not emit md:/lg:
// padding variants, so tablet/desktop overrides use media queries with DS
// spacing tokens (var(--size-*)).
// ---------------------------------------------------------------------------
@media (min-width: 768px) {
  .ca-highlight-section {
    padding-top: var(--size-9);
    padding-bottom: var(--size-9);
  }
}

@media (min-width: 1024px) {
  .ca-highlight-section {
    padding-top: var(--size-11);
    padding-bottom: var(--size-11);
  }
}

.ca-highlight-section :deep(.button-text) {
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
}

// ---------------------------------------------------------------------------
// Highlight section background variants (from the model's backgroundColor).
// Applied to a full-width wrapper so the colour spans edge-to-edge, outside
// the horizontal padding of .ca-bullet-container.
//
// Each variant also re-tints the shimmer skeletons via the
// --ca-shimmer-from / --ca-shimmer-to custom properties, which cascade down
// to every child component's self-contained shimmer element.
// ---------------------------------------------------------------------------
.ca-highlight-section--green {
  background: var(--ca-color-backgrounds-container-high);
  --ca-shimmer-angle: 270deg;
  --ca-shimmer-from: var(
    --Colors-CAOnBackground-Inverted_10,
    rgba(255, 255, 255, 0.1)
  );
  --ca-shimmer-to: var(--Colors-CATheme-Primary_20, rgba(6, 101, 66, 0.2));
}

.ca-highlight-section--solarized {
  background: var(--ca-color-backgrounds-container);
  --ca-shimmer-angle: 90deg;
  --ca-shimmer-from: var(--Colors-CAEmpresas-SolarizedDark, #e5e3df);
  --ca-shimmer-to: var(--Colors-CAContainer-BGContainer, #f6f7f2);
}

// Shimmer placeholder for the CTA button. Self-contained: gradient and
// animation are defined locally, with the --ca-shimmer-* custom properties (set
// by the section variants above) tinting it; the fallbacks keep it working
// standalone.
.ca-highlight-section__button-shimmer {
  width: 160px;
  height: var(--size-9);
  border-radius: 100px;
  background-image: linear-gradient(
    var(--ca-shimmer-angle, 90deg),
    var(--ca-shimmer-from, var(--ca-color-empresas-solarized-medium, #edecea))
      0%,
    var(--ca-shimmer-to, var(--ca-color-backgrounds-container)) 100%
  );
  background-size: 200% 100%;
  animation: ca-highlight-section-shimmer 1.5s ease-in-out infinite;
}

@keyframes ca-highlight-section-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// ---------------------------------------------------------------------------
// Container (mobile first) — layout uses DS utility classes in the template
// (flex, flex-col, p-size-4, gap-size-3, rounded-size-4).
// ---------------------------------------------------------------------------
// Odd number of cards: the last one spans both columns (mobile 2-col grid).
.ca-quick-action-cards__card--full {
  grid-column: 1 / -1;
}

// Tablet (>= 768px): two columns, keeping the previous behaviour.
// The doubled class raises specificity to (0,3,0) so it beats the DS
// `.ds-scope .grid-cols-1` utility (0,2,0), which is always active and would
// otherwise force a single column at every breakpoint.
@media (min-width: 768px) {
  .ca-quick-action-cards.ca-quick-action-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--size-4);
  }
}

@media (min-width: 1024px) {
  // Desktop padding uses the DS spacing token (the lg:p-size-8 utility
  // isn't emitted by the DS stylesheet nor by the local Tailwind config).
  // Same doubled-class specificity bump as the tablet rule; being later in
  // source order it also wins over the tablet rule at desktop widths.
  .ca-quick-action-cards.ca-quick-action-cards {
    // As many columns as cards (capped at 4) so 1/2/3 cards fill the width.
    grid-template-columns: repeat(var(--ca-desktop-cols, 4), minmax(0, 1fr));
    gap: var(--size-5);
  }

  // On desktop, don't force the last card to full width.
  .ca-quick-action-cards__card--full {
    grid-column: auto;
  }
}

// ---------------------------------------------------------------------------
// Container background variants
// ---------------------------------------------------------------------------
.ca-quick-action-cards--white {
  background: var(--ca-color-neutrals-inverted);
}

.ca-quick-action-cards--solarized {
  background: var(--ca-color-backgrounds-container);
}

// ---------------------------------------------------------------------------
// Rounded corners variants
// ---------------------------------------------------------------------------
.ca-quick-action-cards--rounded-top {
  border-top-left-radius: var(--size-5);
  border-top-right-radius: var(--size-5);
}

.ca-quick-action-cards--rounded-bottom {
  border-bottom-left-radius: var(--size-5);
  border-bottom-right-radius: var(--size-5);
}

@media (min-width: 1024px) {
  .ca-quick-action-cards--rounded-top {
    border-top-left-radius: var(--size-8);
    border-top-right-radius: var(--size-8);
  }

  .ca-quick-action-cards--rounded-bottom {
    border-bottom-left-radius: var(--size-8);
    border-bottom-right-radius: var(--size-8);
  }
}
</style>
